import {
  CREDIT_PLANS,
  TOPUP_PACKS,
  type CreditPlan,
  type TopupPack,
} from "@super-resume-new/auth/lib/plans";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import { Tabs, TabsList, TabsPanel, TabsTab } from "@super-resume-new/ui/components/tabs";
import { cn } from "@super-resume-new/ui/lib/utils";
import { usePostHog } from "@posthog/react/slim";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  ArrowUpIcon,
  InfinityIcon,
  PlusIcon,
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  ClockIcon,
  CoinsIcon,
  GiftIcon,
  SparklesIcon,
  Wand2Icon,
  ZapIcon,
} from "lucide-react";
import { useState } from "react";

import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { getLedger, type LedgerEntry } from "@/functions/get-ledger";
import { client } from "@/utils/orpc";
import { getUser } from "@/functions/get-user";
import { openPortal, startCheckout } from "@/lib/checkout";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/credits")({
  beforeLoad: async () => {
    const session = await getUser();
    if (!session) {
      throw redirect({ to: "/" });
    }
    return { session };
  },
  head: () =>
    seo({
      title: "Credits & Billing | JobCues",
      description:
        "Your credit balance, plan, and usage ledger across resume tailoring and the ATS booster.",
      path: "/credits",
      // Signed-out visitors are redirected to the home page, so this URL can
      // only ever render an empty page to a crawler.
      noindex: true,
    }),
  loader: async () => {
    return await getLedger();
  },
  component: RouteComponent,
});

const REASON_LABEL: Record<string, string> = {
  signup_grant: "Welcome bonus",
  purchase: "Credits purchased",
  rewrite_debit: "Resume Tailor",
  ats_booster_debit: "ATS Booster",
  expiry: "Monthly credits expired",
  refund: "Refund",
};

const REASON_ICON: Record<string, React.ReactNode> = {
  signup_grant: <GiftIcon className="size-3.5" aria-hidden="true" />,
  purchase: <ArrowUpIcon className="size-3.5" aria-hidden="true" />,
  rewrite_debit: <Wand2Icon className="size-3.5 text-muted-foreground" aria-hidden="true" />,
  ats_booster_debit: <ZapIcon className="size-3.5 text-emerald-500" aria-hidden="true" />,
  expiry: <ClockIcon className="size-3.5 text-muted-foreground" aria-hidden="true" />,
  refund: <ArrowUpIcon className="size-3.5" aria-hidden="true" />,
};

function formatDate(d: Date | string) {
  const date = typeof d === "string" ? new Date(d) : d;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function formatDay(d: Date | string) {
  const date = typeof d === "string" ? new Date(d) : d;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function formatDayHeader(dateStr: string) {
  const date = new Date(dateStr + "T00:00:00");
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isToday = date.toDateString() === today.toDateString();
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isToday) return "Today";
  if (isYesterday) return "Yesterday";

  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

interface DayAggregate {
  dateStr: string;
  displayDate: string;
  totalSpent: number;
  resumeTailorSpent: number;
  atsBoosterSpent: number;
  creditsGranted: number;
  entries: LedgerEntry[];
}

function aggregateEntriesByDay(entries: LedgerEntry[]): DayAggregate[] {
  const map = new Map<string, DayAggregate>();

  for (const entry of entries) {
    const dateObj = new Date(entry.createdAt);
    // YYYY-MM-DD key using local date parts
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;

    if (!map.has(dateStr)) {
      map.set(dateStr, {
        dateStr,
        displayDate: formatDayHeader(dateStr),
        totalSpent: 0,
        resumeTailorSpent: 0,
        atsBoosterSpent: 0,
        creditsGranted: 0,
        entries: [],
      });
    }

    const dayGroup = map.get(dateStr)!;
    dayGroup.entries.push(entry);

    if (entry.delta < 0) {
      const amount = Math.abs(entry.delta);
      dayGroup.totalSpent += amount;
      if (entry.reason === "ats_booster_debit") {
        dayGroup.atsBoosterSpent += amount;
      } else {
        // default/rewrite_debit -> Resume Tailor
        dayGroup.resumeTailorSpent += amount;
      }
    } else {
      dayGroup.creditsGranted += entry.delta;
    }
  }

  // Return sorted newest first
  return Array.from(map.values()).sort(
    (a, b) => new Date(b.dateStr).getTime() - new Date(a.dateStr).getTime(),
  );
}

function RouteComponent() {
  const posthog = usePostHog();
  const { session } = Route.useRouteContext();
  const { entries: initial, balance, totalResumeTailor, totalAtsBooster, totalGranted, nextCursor: initialCursor, activePlan } = Route.useLoaderData();
  const currentPlan = CREDIT_PLANS.find((p) => p.slug === activePlan?.slug);
  const [activeTab, setActiveTab] = useState("plans");
  const [expandedDays, setExpandedDays] = useState<Record<string, boolean>>({});
  const [entries, setEntries] = useState<LedgerEntry[]>(initial);
  const [nextCursor, setNextCursor] = useState<number | null>(initialCursor);
  const [loadingMore, setLoadingMore] = useState(false);

  const dailyAggregates = aggregateEntriesByDay(entries);

  async function handleLoadMore() {
    if (!nextCursor) return;
    setLoadingMore(true);
    const page = await client.credits.ledger({ cursor: nextCursor });
    setEntries((prev) => [...prev, ...page.entries]);
    setNextCursor(page.nextCursor);
    setLoadingMore(false);
  }

  const toggleDayExpanded = (dateStr: string) => {
    setExpandedDays((prev) => ({ ...prev, [dateStr]: !prev[dateStr] }));
  };

  return (
    <PageLayout id="main-content" as="div" className="max-w-7xl">
      <PageHeader
        title="Credits and plans"
        description={session?.user?.email}
        icon={CoinsIcon}
        actions={
          <div className="text-right">
            <p className="font-mono text-3xl font-semibold leading-tight tabular-nums text-foreground">
              {balance}
            </p>
            <p className="text-xs font-semibold text-muted-foreground">
              Credits
            </p>
          </div>
        }
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="gap-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTab value="plans" className="flex items-center gap-2 py-2">
            <SparklesIcon className="size-3.5 text-violet-500" aria-hidden="true" />
            Plans
          </TabsTab>
          <TabsTab value="usage" className="flex items-center gap-2 py-2">
            <CalendarIcon className="size-3.5" aria-hidden="true" />
            Usage
          </TabsTab>
        </TabsList>

        <TabsPanel value="plans">
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border p-6">
              <div>
                <h2 className="text-sm font-semibold text-foreground">Plans and upgrades</h2>
                <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
                  {currentPlan
                    ? `You are on ${currentPlan.name}. Credits refresh on every billing date.`
                    : "Pick a monthly plan for AI resume rewrites and MCP or API automation."}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {activePlan?.nextBillingDate && (
                  <Badge variant="outline" className="text-[11px]">
                    <CalendarIcon className="mr-1 size-3" aria-hidden="true" />
                    Renews {formatDay(activePlan.nextBillingDate)}
                  </Badge>
                )}
                {activePlan && (
                  <Button variant="outline" size="sm" onClick={() => void openPortal()}>
                    Manage subscription
                  </Button>
                )}
              </div>
            </div>

            <div className="grid divide-y divide-border md:grid-cols-3 md:divide-x md:divide-y-0">
              {CREDIT_PLANS.map((plan) => (
                <PlanCard
                  key={plan.slug}
                  plan={plan}
                  isCurrent={plan.slug === activePlan?.slug}
                  onCheckout={() => {
                    posthog.capture("checkout_started", { plan: plan.slug });
                    void startCheckout(plan.slug);
                  }}
                />
              ))}
            </div>

            <div className="border-t border-border p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold text-foreground">
                  <PlusIcon
                    className="mr-1.5 inline size-3.5 align-[-0.15em] text-violet-500"
                    aria-hidden="true"
                  />
                  One-time top-up
                </h3>
                <p className="text-[13px] text-muted-foreground">
                  Out of credits before your renewal? Buy a pack — no plan change, no renewal.
                </p>
              </div>
              <p className="mt-2 text-[13px] text-muted-foreground">
                <InfinityIcon
                  className="mr-1.5 inline size-3.5 align-[-0.15em] text-violet-500"
                  aria-hidden="true"
                />
                Top-up credits never expire. They stay on your account until you spend them, and
                are kept separate from the monthly credits your plan grants.
              </p>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                {TOPUP_PACKS.map((pack) => (
                  <TopupCard
                    key={pack.slug}
                    pack={pack}
                    onCheckout={() => {
                      posthog.capture("checkout_started", { plan: pack.slug });
                      void startCheckout(pack.slug);
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </TabsPanel>

        <TabsPanel value="usage" className="flex flex-col gap-8">
          {/* Usage stats */}
          <div className="grid divide-y divide-border overflow-hidden rounded-xl border border-border bg-card sm:grid-cols-2 sm:divide-x sm:divide-y-0">
            <div className="flex flex-col justify-between gap-3 p-6">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Total credits used
                </p>
                <p className="mt-2 font-mono text-3xl font-semibold tabular-nums text-foreground">
                  {totalResumeTailor + totalAtsBooster}
                </p>
              </div>
              <div className="flex flex-col gap-1.5 border-t border-border pt-3">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-muted-foreground">
                    <Wand2Icon
                      className="mr-1.5 inline size-3.5 align-[-0.15em]"
                      aria-hidden="true"
                    />
                    Resume tailor
                  </span>
                  <span className="font-mono text-foreground">{totalResumeTailor}</span>
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-muted-foreground">
                    <ZapIcon className="mr-1.5 inline size-3.5 align-[-0.15em]" aria-hidden="true" />
                    ATS booster
                  </span>
                  <span className="font-mono text-foreground">{totalAtsBooster}</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-between gap-3 p-6">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Total granted or purchased
                </p>
                <p className="mt-2 font-mono text-3xl font-semibold tabular-nums text-emerald-500">
                  +{totalGranted}
                </p>
              </div>
              <p className="border-t border-border pt-3 text-[11px] text-muted-foreground">
                Includes your welcome bonus and every monthly plan refresh.
              </p>
            </div>
          </div>

          {/* Daily Aggregated Credit Usage History */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground">
                <CalendarIcon className="mr-1.5 inline size-3.5 align-[-0.15em]" aria-hidden="true" />
                Credit usage and activity
              </h2>
              <span className="text-[11px] text-muted-foreground">Aggregated by day</span>
            </div>

            {dailyAggregates.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-8 text-center text-[13px] text-muted-foreground">
                No transaction history yet.
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {dailyAggregates.map((day) => {
                  const isExpanded = !!expandedDays[day.dateStr];
                  return (
                    <div
                      key={day.dateStr}
                      className="overflow-hidden rounded-xl border border-border bg-card transition"
                    >
                      {/* Day Header Summary */}
                      <button
                        type="button"
                        onClick={() => toggleDayExpanded(day.dateStr)}
                        aria-expanded={isExpanded}
                        className="w-full flex flex-wrap items-center justify-between gap-3 p-4 cursor-pointer text-left hover:bg-muted/40 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex size-9 items-center justify-center rounded-lg border border-border bg-muted">
                            <CalendarIcon className="size-4 text-muted-foreground" aria-hidden="true" />
                          </div>
                          <div>
                            <h3 className="text-sm font-semibold text-foreground">{day.displayDate}</h3>
                            <p className="text-[11px] text-muted-foreground">
                              {day.entries.length}{" "}
                              {day.entries.length === 1 ? "transaction" : "transactions"}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2.5 flex-wrap">
                          {/* Product breakdown pills */}
                          {day.resumeTailorSpent > 0 && (
                            <Badge variant="outline" className="font-mono text-[11px]">
                              <Wand2Icon className="mr-1 size-3" aria-hidden="true" />
                              Tailor −{day.resumeTailorSpent}
                            </Badge>
                          )}

                          {day.atsBoosterSpent > 0 && (
                            <Badge variant="outline" className="font-mono text-[11px]">
                              <ZapIcon className="mr-1 size-3" aria-hidden="true" />
                              ATS −{day.atsBoosterSpent}
                            </Badge>
                          )}

                          {day.creditsGranted > 0 && (
                            <Badge
                              variant="outline"
                              className="border-emerald-500/30 bg-emerald-500/10 font-mono text-[11px] text-emerald-500"
                            >
                              <GiftIcon className="mr-1 size-3" aria-hidden="true" />+
                              {day.creditsGranted}
                            </Badge>
                          )}

                          {day.totalSpent > 0 && (
                            <span className="ml-1 font-mono text-[13px] tabular-nums text-foreground">
                              −{day.totalSpent}
                            </span>
                          )}

                          <ChevronDownIcon
                            className={cn(
                              "size-4 text-muted-foreground transition-transform duration-200 ml-1",
                              isExpanded && "rotate-180",
                            )}
                          />
                        </div>
                      </button>

                      {/* Expandable detailed log for this day */}
                      {isExpanded && (
                        <div className="flex flex-col gap-2 border-t border-border bg-muted/40 p-4">
                          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Activity log
                          </p>
                          <ul className="flex flex-col gap-1.5">
                            {day.entries.map((entry) => (
                              <li
                                key={entry.id}
                                className="flex items-center justify-between rounded-lg border border-border bg-card px-3.5 py-2.5 text-[13px]"
                              >
                                <div className="flex items-center gap-2.5">
                                  <span
                                    className={cn(
                                      "flex size-6 items-center justify-center rounded-full border border-border",
                                      entry.delta > 0
                                        ? "bg-emerald-500/10 text-emerald-500"
                                        : "bg-muted text-muted-foreground",
                                    )}
                                  >
                                    {REASON_ICON[entry.reason] ?? (
                                      <CoinsIcon className="size-3" aria-hidden="true" />
                                    )}
                                  </span>
                                  <span className="font-medium text-foreground">
                                    {REASON_LABEL[entry.reason] ?? entry.reason}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-muted-foreground">
                                    {formatDate(entry.createdAt)}
                                  </span>
                                  <span
                                    className={cn(
                                      "font-mono tabular-nums",
                                      entry.delta > 0 ? "text-emerald-500" : "text-foreground",
                                    )}
                                  >
                                    {entry.delta > 0 ? "+" : ""}
                                    {entry.delta}
                                  </span>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {nextCursor && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="w-full"
              >
                {loadingMore ? "Loading…" : "Load more"}
              </Button>
            )}
          </div>
        </TabsPanel>
      </Tabs>
    </PageLayout>
  );
}

function PlanCard({
  plan,
  isCurrent,
  onCheckout,
}: {
  plan: CreditPlan;
  isCurrent: boolean;
  onCheckout: () => void;
}) {
  return (
    <div
      className={cn(
        "flex flex-col justify-between p-6",
        plan.featured && "bg-muted/40",
        isCurrent && "bg-violet-500/8",
      )}
    >
      <div>
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-foreground">{plan.name}</h3>
          <Badge
            variant="outline"
            className={cn(
              "font-mono text-[11px]",
              (plan.featured || isCurrent) &&
                "border-violet-500/30 bg-violet-500/10 text-violet-500",
            )}
          >
            {isCurrent ? (
              <>
                <CheckIcon className="mr-1 size-3" aria-hidden="true" />
                Current plan
              </>
            ) : plan.featured ? (
              "Most popular"
            ) : (
              `${plan.credits} credits`
            )}
          </Badge>
        </div>
        <div className="mt-3 flex items-baseline gap-1">
          <span className="font-mono text-3xl font-semibold text-foreground">${plan.priceUsd}</span>
          <span className="text-[11px] text-muted-foreground">/ month</span>
        </div>
        <ul className="mt-4 space-y-2 text-[13px] text-muted-foreground">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2">
              <CheckIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
      <Button
        onClick={onCheckout}
        variant={plan.featured && !isCurrent ? "default" : "outline"}
        size="sm"
        disabled={isCurrent}
        className="mt-6 w-full"
      >
        {isCurrent ? "Your current plan" : plan.cta}
      </Button>
    </div>
  );
}

function TopupCard({ pack, onCheckout }: { pack: TopupPack; onCheckout: () => void }) {
  return (
    <button
      type="button"
      onClick={onCheckout}
      className="group flex flex-col items-start rounded-xl border border-border bg-muted/30 p-4 text-left transition-colors hover:border-violet-500/40 hover:bg-violet-500/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/50"
    >
      <span className="flex w-full items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-foreground">{pack.name}</span>
        <span className="font-mono text-lg font-semibold tabular-nums text-foreground">
          ${pack.priceUsd}
        </span>
      </span>
      <span className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">{pack.body}</span>
      <span className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground group-hover:text-violet-500">
        Buy once
        <ArrowUpIcon
          className="ml-1 inline size-3 rotate-45 align-[-0.1em] transition-transform group-hover:translate-x-0.5"
          aria-hidden="true"
        />
      </span>
    </button>
  );
}
